/* global axios */
import ApiClient from './ApiClient';

class InstagramMediaReportsAPI extends ApiClient {
  constructor() {
    super('instagram_media_reports', { accountScoped: true, apiVersion: 'v2' });
  }

  getReport({ from, to, inboxId, groupBy }) {
    return axios.get(`${this.url}`, {
      params: {
        since: from,
        until: to,
        inbox_id: inboxId,
        group_by: groupBy,
      },
    });
  }

  // Media Gallery
  getMediaList({ inboxId, page, mediaType, from, to }) {
    return axios.get(`${this.url}/media_list`, {
      params: {
        inbox_id: inboxId,
        page,
        media_type: mediaType,
        since: from,
        until: to,
      },
    });
  }

  publishMedia({ inboxId, mediaUrl, caption, mediaType, children }) {
    return axios.post(`${this.url}/publish_media`, {
      inbox_id: inboxId,
      media_url: mediaUrl,
      caption,
      media_type: mediaType,
      children,
    });
  }

  deleteMedia({ inboxId, mediaId }) {
    return axios.delete(`${this.url}/media/${mediaId}`, {
      params: { inbox_id: inboxId },
    });
  }

  saveMedia({ inboxId, mediaId }) {
    return axios.post(`${this.url}/media/${mediaId}/save`, {
      inbox_id: inboxId,
    });
  }

  generateRepostLink({ inboxId, mediaId }) {
    return axios.post(`${this.url}/media/${mediaId}/repost_link`, {
      inbox_id: inboxId,
    });
  }

  tracker({ inboxId, prompt }) {
    return axios.post(`${this.url}/tracker`, {
      inbox_id: inboxId,
      prompt,
    });
  }

  // Monitored Profiles
  getMonitoredProfiles({ inboxId, profileType }) {
    return axios.get(`${this.url}/monitored_profiles`, {
      params: { inbox_id: inboxId, profile_type: profileType },
    });
  }

  createMonitoredProfile({
    inboxId,
    username,
    keywords,
    profileType,
    candidateName,
    notes,
    priority,
    facebookPageId,
    monitorFacebook,
    phoneNumber,
  }) {
    return axios.post(`${this.url}/create_monitored_profile`, {
      inbox_id: inboxId,
      username,
      keywords,
      profile_type: profileType,
      candidate_name: candidateName,
      notes,
      priority,
      facebook_page_id: facebookPageId,
      monitor_facebook: monitorFacebook,
      phone_number: phoneNumber,
    });
  }

  updateMonitoredProfile({
    inboxId,
    profileId,
    username,
    keywords,
    active,
    profileType,
    candidateName,
    notes,
    priority,
    facebookPageId,
    monitorFacebook,
    phoneNumber,
  }) {
    return axios.patch(`${this.url}/monitored_profiles/${profileId}`, {
      inbox_id: inboxId,
      username,
      keywords,
      active,
      profile_type: profileType,
      candidate_name: candidateName,
      notes,
      priority,
      facebook_page_id: facebookPageId,
      monitor_facebook: monitorFacebook,
      phone_number: phoneNumber,
    });
  }

  deleteMonitoredProfile({ inboxId, profileId }) {
    return axios.delete(`${this.url}/monitored_profiles/${profileId}`, {
      params: { inbox_id: inboxId },
    });
  }

  checkProfileNow({ inboxId, profileId }) {
    return axios.post(`${this.url}/monitored_profiles/${profileId}/check`, {
      inbox_id: inboxId,
    });
  }

  // Alerts
  getAllAlerts({
    inboxId,
    sentiment,
    riskLevel,
    platform,
    profileType,
    unread,
    sinceDate,
  }) {
    return axios.get(`${this.url}/all_alerts`, {
      params: {
        inbox_id: inboxId,
        sentiment,
        risk_level: riskLevel,
        platform,
        profile_type: profileType,
        unread,
        since_date: sinceDate,
      },
    });
  }

  markAlertRead({ inboxId, alertId }) {
    return axios.patch(`${this.url}/alerts/${alertId}/read`, {
      inbox_id: inboxId,
    });
  }

  // Dashboard
  getDashboardStats({ inboxId, sinceDate }) {
    return axios.get(`${this.url}/dashboard_stats`, {
      params: { inbox_id: inboxId, since_date: sinceDate },
    });
  }

  // Global Listening
  getListeningConfig({ inboxId }) {
    return axios.get(`${this.url}/listening_config`, {
      params: { inbox_id: inboxId },
    });
  }

  saveListeningConfig({
    inboxId,
    candidateName,
    candidateUsername,
    hashtags,
    watchTerms,
    adversaryNames,
    active,
    aiEnabled,
  }) {
    return axios.post(`${this.url}/save_listening_config`, {
      inbox_id: inboxId,
      candidate_name: candidateName,
      candidate_username: candidateUsername,
      hashtags,
      watch_terms: watchTerms,
      adversary_names: adversaryNames,
      active,
      ai_enabled: aiEnabled,
    });
  }

  runListening({ inboxId }) {
    return axios.post(`${this.url}/run_listening`, {
      inbox_id: inboxId,
    });
  }

  // Repost Tracking
  getRepostTrackings({ inboxId, status }) {
    return axios.get(`${this.url}/repost_trackings`, {
      params: { inbox_id: inboxId, status },
    });
  }

  refreshOfficialPosts({ inboxId }) {
    return axios.post(`${this.url}/refresh_official_posts`, {
      inbox_id: inboxId,
    });
  }

  dismissRepostEntry({ inboxId, entryId }) {
    return axios.patch(
      `${this.url}/repost_tracking_entries/${entryId}/dismiss`,
      {
        inbox_id: inboxId,
      }
    );
  }

  // Leadership & Audit
  getLeadershipRanking({ inboxId, period }) {
    return axios.get(`${this.url}/leadership_ranking`, {
      params: { inbox_id: inboxId, period },
    });
  }

  getAuditedPosts({ inboxId, classification, page }) {
    return axios.get(`${this.url}/audited_posts`, {
      params: { inbox_id: inboxId, classification, page },
    });
  }

  classifyAlert({ inboxId, alertId, classification }) {
    return axios.patch(`${this.url}/alerts/${alertId}/classify`, {
      inbox_id: inboxId,
      classification,
    });
  }

  // Radar
  getRadarStats({ inboxId }) {
    return axios.get(`${this.url}/radar_stats`, {
      params: { inbox_id: inboxId },
    });
  }

  getRadarComments({ inboxId, page }) {
    return axios.get(`${this.url}/radar_comments`, {
      params: { inbox_id: inboxId, page },
    });
  }

  getRadarTimeline({ inboxId }) {
    return axios.get(`${this.url}/radar_timeline`, {
      params: { inbox_id: inboxId },
    });
  }

  // Political Map
  getPoliticalMapEntries({ inboxId }) {
    return axios.get(`${this.url}/political_map_entries`, {
      params: { inbox_id: inboxId },
    });
  }

  createPoliticalMapEntry({ inboxId, value, entryType, origin }) {
    return axios.post(`${this.url}/political_map_entries`, {
      inbox_id: inboxId,
      value,
      entry_type: entryType,
      origin,
    });
  }

  updatePoliticalMapEntry({ inboxId, entryId, validated }) {
    return axios.patch(`${this.url}/political_map_entries/${entryId}`, {
      inbox_id: inboxId,
      validated,
    });
  }

  deletePoliticalMapEntry({ inboxId, entryId }) {
    return axios.delete(`${this.url}/political_map_entries/${entryId}`, {
      params: { inbox_id: inboxId },
    });
  }

  getPoliticalMapSuggestions({ inboxId }) {
    return axios.post(`${this.url}/political_map_suggestions`, {
      inbox_id: inboxId,
    });
  }

  importPoliticalMap({ inboxId, entries }) {
    return axios.post(`${this.url}/political_map_import`, {
      inbox_id: inboxId,
      entries,
    });
  }

  // Credits
  getMonitorCredits({ inboxId, page }) {
    return axios.get(`${this.url}/monitor_credits`, {
      params: { inbox_id: inboxId, page },
    });
  }

  getMonitorCreditsBalance({ inboxId }) {
    return axios.get(`${this.url}/monitor_credits_balance`, {
      params: { inbox_id: inboxId },
    });
  }

  // Monthly Report
  getMonthlyReport({ inboxId, month, year }) {
    return axios.get(`${this.url}/monthly_report`, {
      params: { inbox_id: inboxId, month, year },
    });
  }

  getAnalytics({ inboxId, period }) {
    return axios.get(`${this.url}/analytics`, {
      params: { inbox_id: inboxId, period },
    });
  }

  getBotDetection({ inboxId }) {
    return axios.get(`${this.url}/bot_detection`, {
      params: { inbox_id: inboxId },
    });
  }

  generateSpeech({ inboxId, type, topic, context, tone }) {
    return axios.post(`${this.url}/generate_speech`, {
      inbox_id: inboxId,
      type,
      topic,
      context,
      tone,
    });
  }

  suggestTopics({ inboxId }) {
    return axios.get(`${this.url}/suggest_topics`, {
      params: { inbox_id: inboxId },
    });
  }

  generateSurvey({ inboxId, topic, count }) {
    return axios.post(`${this.url}/generate_survey`, {
      inbox_id: inboxId,
      topic,
      count,
    });
  }

  sendSurvey({ inboxId, contactIds, questions, surveyName }) {
    return axios.post(`${this.url}/send_survey`, {
      inbox_id: inboxId,
      contact_ids: contactIds,
      questions,
      survey_name: surveyName,
    });
  }

  analyzeSurvey({ inboxId, surveyName }) {
    return axios.post(`${this.url}/analyze_survey`, {
      inbox_id: inboxId,
      survey_name: surveyName,
    });
  }

  analyzeImage({ inboxId, imageUrl }) {
    return axios.post(`${this.url}/analyze_image`, {
      inbox_id: inboxId,
      image_url: imageUrl,
    });
  }
}

export default new InstagramMediaReportsAPI();
